<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  email,
  minLength,
  requiredIf,
  sameAs,
} from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import { generateStrongPassword } from 'dashboard/helper/passwordHelper';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');
// Creating the account with a password skips the invitation email entirely:
// the person is confirmed straight away and the admin hands over the login.
const setPasswordManually = ref(false);
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const selectedOrgUnitId = ref(null);
const selectedAccessRoleId = ref(null);

const passwordRules = {
  required: requiredIf(() => setPasswordManually.value),
  minLength: minLength(8),
  // Chatwoot requires one uppercase, one lowercase, one digit and one symbol.
  hasRequiredContent: value =>
    !setPasswordManually.value ||
    (/[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[^A-Za-z0-9]/.test(value)),
};

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
  agentPassword: passwordRules,
  agentPasswordConfirmation: {
    sameAsPassword: sameAs(agentPassword),
  },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentPassword,
  agentPasswordConfirmation,
});

const orgUnits = useMapGetter('orgAccess/getUnits');
const accessRoles = useMapGetter('orgAccess/getRoles');

onMounted(() => {
  // Best effort: an agent without settings access simply gets no dropdowns.
  store.dispatch('orgAccess/fetchUnits').catch(() => {});
  store.dispatch('orgAccess/fetchRoles').catch(() => {});
});

const generatePassword = () => {
  const generated = generateStrongPassword();
  agentPassword.value = generated;
  agentPasswordConfirmation.value = generated;
};

const copyPassword = () => {
  navigator.clipboard?.writeText(agentPassword.value);
  useAlert(t('AGENT_MGMT.ADD.FORM.PASSWORD.COPIED'));
};

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (setPasswordManually.value) {
      payload.password = agentPassword.value;
      payload.password_confirmation =
        agentPasswordConfirmation.value || agentPassword.value;
    }

    if (selectedOrgUnitId.value) {
      payload.org_unit_id = selectedOrgUnitId.value;
      payload.access_role_id = selectedAccessRoleId.value;
    }

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label class="flex items-center gap-2 mb-2 text-sm cursor-pointer">
          <input v-model="setPasswordManually" type="checkbox" class="!mb-0" />
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.TOGGLE') }}
        </label>
        <p class="mb-2 text-xs text-n-slate-11">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.HINT') }}
        </p>
      </div>

      <template v-if="setPasswordManually">
        <div class="w-full">
          <label :class="{ error: v$.agentPassword.$error }">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
            <input
              v-model="agentPassword"
              type="text"
              autocomplete="off"
              :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
              @input="v$.agentPassword.$touch"
            />
            <span v-if="v$.agentPassword.$error" class="message">
              {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.agentPasswordConfirmation.$error }">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.CONFIRM_LABEL') }}
            <input
              v-model="agentPasswordConfirmation"
              type="text"
              autocomplete="off"
              @input="v$.agentPasswordConfirmation.$touch"
            />
            <span v-if="v$.agentPasswordConfirmation.$error" class="message">
              {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.CONFIRM_ERROR') }}
            </span>
          </label>
        </div>

        <div class="flex gap-2 pb-3">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            :label="$t('AGENT_MGMT.ADD.FORM.PASSWORD.GENERATE')"
            @click.prevent="generatePassword"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            :disabled="!agentPassword"
            :label="$t('AGENT_MGMT.ADD.FORM.PASSWORD.COPY')"
            @click.prevent="copyPassword"
          />
        </div>
      </template>

      <div v-if="orgUnits.length" class="w-full">
        <label>
          {{ $t('AGENT_MGMT.ADD.FORM.ORG_UNIT.LABEL') }}
          <select v-model="selectedOrgUnitId">
            <option :value="null">
              {{ $t('AGENT_MGMT.ADD.FORM.ORG_UNIT.NONE') }}
            </option>
            <option v-for="unit in orgUnits" :key="unit.id" :value="unit.id">
              {{ unit.name }}
            </option>
          </select>
        </label>
      </div>

      <div v-if="selectedOrgUnitId" class="w-full">
        <label>
          {{ $t('AGENT_MGMT.ADD.FORM.ACCESS_ROLE.LABEL') }}
          <select v-model="selectedAccessRoleId">
            <option :value="null">
              {{ $t('AGENT_MGMT.ADD.FORM.ACCESS_ROLE.NONE') }}
            </option>
            <option v-for="role in accessRoles" :key="role.id" :value="role.id">
              {{ role.name }}
            </option>
          </select>
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
